import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO } from "../../lib/site";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Categories", id: "categories" },
  { label: "Why Us", id: "why" },
  { label: "Process", id: "process" },
  { label: "Founders", id: "founders" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "py-5 bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo-btn"
          onClick={() => go("hero")}
          className="flex items-center gap-3 group"
        >
          <img src={LOGO} alt="AM Ventures" className="h-11 w-11 object-contain rounded-md" />
          <span className="font-display text-lg font-semibold tracking-wide text-white">
            AM <span className="text-gold">VENTURES</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="relative text-sm text-slate-200 hover:text-white transition-colors group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-contact-cta"
            onClick={() => go("contact")}
            className="rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-[#0B1F3A] transition-all duration-300 hover:bg-[#DDBE94] hover:scale-105"
          >
            Get in Touch
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden glass mt-3 mx-4 rounded-2xl"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-5 gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-slate-200 hover:text-gold border-b border-white/5 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-contact-cta"
                onClick={() => go("contact")}
                className="mt-4 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-[#0B1F3A]"
              >
                Get in Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export { scrollTo };
